/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import DriversCountFilter from "pages/Front/Booking/components/BookingFilters/DriversCountFilter";
import BookingDateFilter from "pages/Front/Booking/components/BookingFilters/BookingDateFilter";
import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {BookingFilter} from "types/app";
import {Package} from "types/entities/Package";
import BookingAction from "store/booking/BookingAction";
import {useDispatch} from "useDispatch";
import {RouteComponentProps, withRouter} from "react-router";
import {useTranslation} from "react-i18next";
import ButtonSubmit from "pages/Front/Booking/components/ButtonSubmit";
import {useTypedSelector} from "helpers/reducers";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import ActivityFilter from "pages/Front/Booking/components/BookingFilters/ActivityFilter";
import {closeModal} from "helpers/actions";
import {packagesCartTimeout, translate} from "utils/utility";
import Dates from "utils/Dates";
import {filterFitsWithPackageRequirements, getPersonsTypeFraction, packageHasUpsellItems} from "utils/packageUtils";
import GroupTypeFilter from "./GroupTypeFilter";
import BookingVoucher from "pages/Front/Booking/BookingVoucher";
import {ThunkAction} from "redux-thunk";
import {PackagesCart} from "types/entities/PackagesCart";
import {toast} from "react-toastify";
import {Tooltip} from "react-tooltip";
import SubmitButtonPersonsRequirement
    from "pages/Front/Booking/components/BookingFilters/SubmitButtonPersonsRequirement";
import HoursCountFilter from "./HoursCountFilter";
import useNavigation, {RouteName} from "utils/hooks/useNavigation";
import {ACTIVITIES_SELECTION_ALLOWED, CURRENCY, GROUP_TYPES_ALLOWED} from "helpers/config";
import {useHallOpening} from "utils/hooks/useHallOpening";
import {useVouchersWithValidOnDate} from "utils/voucherUtils";
import {DataLayerItem} from "types/entities/specific/DataLayerItem";
import {GAEventName} from "types/entities/specific/GAEventName";
import {useReactGoogleAnalytics} from "utils/useReactGoogleAnalytics";
import BookingLoginWidget from "pages/Front/Booking/components/BookingLoginWidget";
import {HallCode} from "types/enums/HallCode";
import Loader from "components/Loader";
import {useDebouncedDispatch} from "utils/useDebounceQueue";

interface Params {}

interface BookingFilterProps extends RouteComponentProps<Params> {
    package: Package | null;
    setSelectedBookingFilter?: (BookingFilter) => void;
    proceedToUpsell: boolean;
    children?: React.ReactNode | React.ReactNode[];
    filterMinimal: boolean;
}

const BookingFilterMinimal = (props: BookingFilterProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const hall = useTypedSelector(state => state.hallState.hall);
    const bookingPackage = props.package || useTypedSelector(state => state.bookingState.bookingProcess.package);
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const packages = useTypedSelector(state => state.bookingState.packages);
    const activities = useTypedSelector(state => state.bookingState.activities);
    const dateChosenByCustomer = useTypedSelector(state => state.bookingState.bookingProcess.dateChosenByCustomer) || false;
    // const timeslotsAllowed = props.filterMinimal ? useTypedSelector(state => state.bookingState.timeslotsAllowed) : bookingPackage?.use_timeslots;
    const timeslotsAllowed = props.package?.use_timeslots == 1;
    const [isDisabled, setIsDisabled] = useState(true);
    const [dateWasSetByCustomer, setDateWasSetByCustomer] = useState(props.package?.use_timeslots == 0 || dateChosenByCustomer);
    const totalPersons = +bookingFilter.adults + +bookingFilter.children;
    const {historyPush} = useNavigation();
    const hallOpening = useHallOpening();
    const voucherUtils = useVouchersWithValidOnDate(hallOpening);
    const {sendGenericEvent} = useReactGoogleAnalytics();
    const {debouncedDispatch} = useDebouncedDispatch();

    // getPersonsTypeFraction(totalPersons, props.package.max_drivers_count)

    const defaultValues = bookingFilter || {
        adults: 0,
        children: 0,
        // date: Dates.ymd(hallOpening.hallOpeningDefined && hallOpening.hallOpeningDate ? new Date(hallOpening.hallOpeningDate) : new Date()),
        date: Dates.ymd(new Date()),
        // date: "",
        hours_count: 0,
        activity_ids: [],
        groupType: [],
    };

    //const [formState, setFormState] = useState<BookingFilter>(defaultValues);

    const form = useForm<BookingFilter>({
        defaultValues,
    });

    useEffect(() => {
        form.reset(bookingFilter);
        setIsDisabled(submitDisabled());
    }, [bookingFilter]);

    const submitDisabled = (): boolean => {
        const disabled = filterFitsWithPackageRequirements(bookingPackage, bookingFilter);
        // setIsDisabled(disabled);
        if (!dateWasSetByCustomer) {
            return true;
        }
        return disabled;
        // return false;
    };

    const dispatchInitCart = (): Promise<PackagesCart> => {
        const withCleaup = voucherUtils.getVouchersWithDate();
        return new Promise<PackagesCart>((resolve, reject) => {
            dispatch<ThunkAction<any, any, any, any>>(BookingAction.initCart(withCleaup)).then(r => {
                dispatch(BookingAction.setPackage(props.package));
                resolve(r.packageCart);
            });
        })
            .then(result => {
                return result;
            })
            .catch(error => {
                return error;
            });
    };

    const timedOut = packagesCart => {
        return packagesCartTimeout(packagesCart);
    };
    const bookPackageWithoutTimeslot = (_package, packagesCart) => {
        return new Promise(resolve => {
            resolve(
                dispatch<ThunkAction<any, any, any, any>>(
                    BookingAction.fetchBookPackage(
                        {
                            timeslot_id: null,
                            package_id: _package.id,
                            adults: bookingFilter.adults,
                            children: bookingFilter.children,
                            hours_count: bookingFilter.hours_count,
                            upsell: [],
                        },
                        packagesCart,
                        "BookingFilterMinimal - bookPackageWithoutTimeslot"
                    )
                ).then(r => {
                    return historyPush(RouteName.Customer, "BookingFilterMinimal - bookPackageWithoutTimeslot");
                    // const url = "/booking/customer?" + appendQueryParams(bookingFilter);
                    // props.history.replace(url);
                })
            );
        });
    };

    const resolveCart = () => {
        if (!packagesCart) {
            return dispatchInitCart();
        }
        if (timedOut(packagesCart)) {
            return dispatchInitCart();
        }
        if (packagesCart && !timedOut(packagesCart)) {
            dispatch(BookingAction.setPackage(props.package));
            return packagesCart;
        }
        return dispatchInitCart();
    };
    const initPackagesCart = (): Promise<PackagesCart> => {
        return new Promise<PackagesCart>((resolve, reject) => {
            resolve(resolveCart());
        })
            .then(result => {
                return result;
            })
            .catch(err => {
                console.error("error on initPackage", err);
                throw new Error("InitPackage went wrong");
            });
    };

    const sendGAData = () => {
        const count = bookingFilter.adults + bookingFilter.children;

        if (!props.package) {
            return;
        }
        const value = props.package.price * count;
        const item: DataLayerItem = {
            item_id: props.package.package_id,
            item_name: translate(props.package.name),
            quantity: count,
            item_variant: "",
            price: props.package.price,
            currency: CURRENCY,
        };

        sendGenericEvent(GAEventName.SelectItem, value, [item]);
    };

    const saveBookingFilterHandler = async () => {
        if (props.package === null) {
            return null;
        }

        return Promise.all([initPackagesCart()])
            .then(resolveCheck => {
                const resolvedPackageCart = resolveCheck[0];

                if (timeslotsAllowed) {
                    if (props.package) {
                        // redirectToPackageBooking(props.history, props.package, bookingFilter);
                        const upsellItems = packageHasUpsellItems(props.package);
                        sendGAData();
                        return historyPush(upsellItems ? RouteName.Upsells : RouteName.Timeslots, "BookingFilterMinimal", props.package);
                    } else {
                        toast.error(t("Package not found"));
                    }
                } else {
                    return bookPackageWithoutTimeslot(props.package, resolvedPackageCart);
                }
            })
            .finally(() => {
                dispatch(closeModal());
            });
    };

    const onChangeHoursCount = (type: string, value: string): void => {
        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                [type]: value,
            })
        );
    };

    const onChangeDriversCount = (type: string, value: string): (() => void) => {
        const timeout = setTimeout(() => {
            debouncedDispatch(
                BookingAction.setFilter({
                    ...bookingFilter,
                    [type]: value,
                })
            );
        }, 300);

        return () => {
            clearTimeout(timeout);
        };
    };

    const onChangeDate = (date: string): void => {
        setDateWasSetByCustomer(true);
        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                date: date,
            })
        );
        dispatch(BookingAction.setDateChosenByCustomer(true));
    };

    const onChangeGroupType = (e): void => {
        const newGroupTypeId = [...bookingFilter.groupType];

        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                groupType: !e.target.checked ? newGroupTypeId.filter(groupType => groupType !== e.target.value) : [e.target.value],
            })
        );
    };

    const onChangeActivity = (e): void => {
        const newActivityIds = [...bookingFilter.activity_ids];

        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                activity_ids: !e.target.checked
                    ? newActivityIds.filter(activity => activity !== e.target.value)
                    : [...bookingFilter.activity_ids, e.target.value],
            })
        );
    };

    const chooseHelper = () => {
        if (!dateWasSetByCustomer) {
            return t("You have to select a date");
        }
        if (filterFitsWithPackageRequirements(bookingPackage, bookingFilter)) {
            if (bookingPackage?.extra.bookableByHour) {
                return t("You have to choose amount of hours");
            }
            return t("You have to choose correct amount of persons");
        }
    };

    if (!hall) {
        return <Loader />;
    }

    useEffect(() => {
        // if(props.package && props.package.rewrite_url == "exclusive") {
        // onChangeDriversCount('adults', ""+10)
        // }
    }, [props.package]);

    if (props.filterMinimal) {
        return (
            <div
                className={
                    "BookingFilterMinimal select-none min-w-xs w-full flex flex-col p-4 bg-white rounded-md shadow-md items-stretch justify-center space-y-4"
                }
            >
                {props.package && props.package.rewrite_url != "exclusive" && <>{props.children}</>}
                {props.package && (props.package.rewrite_url == "exclusive" || props.package.extra.bookableByHour) && (
                    <HoursCountFilter
                        package={props.package}
                        register={form.register}
                        setValue={form.setValue}
                        showTitle={true}
                        watch={form.watch}
                        onChange={onChangeHoursCount}
                    />
                )}
                {props.package && (
                    <DriversCountFilter
                        isMinimalistic={false}
                        register={form.register}
                        selectedPackage={props.package}
                        setValue={form.setValue}
                        showTitle={hall.hall_code !== HallCode.ArboPark}
                        watch={form.watch}
                        onChange={onChangeDriversCount}
                    />
                )}

                <div className={!timeslotsAllowed ? "hidden" : ""}>
                    <BookingDateFilter
                        disabled={!dateWasSetByCustomer}
                        package={props.package}
                        register={form.register}
                        setValue={form.setValue}
                        showTitle={true}
                        onChange={onChangeDate}
                    />
                </div>
                <div className={"flex-shrink inline-flex justify-center items-center w-full"} data-for={"chooseHelp"} data-tip={chooseHelper()}>
                    <ButtonSubmit
                        className={"rounded-md"}
                        disabled={submitDisabled()}
                        icon={"check"}
                        id="ButtonSubmitFilter"
                        label={t("Save")}
                        onClick={saveBookingFilterHandler}
                    >
                        {props.package && <SubmitButtonPersonsRequirement total={getPersonsTypeFraction(totalPersons, props.package.min_drivers_count)} />}
                    </ButtonSubmit>
                    <Tooltip data-tooltip-float={true} id={"chooseHelp"} />
                </div>
            </div>
        );
    } else {
        return (
            <div className={"BookingFiltersDesktop sticky top-4 col-span-2 lg:col-span-1 w-full flex flex-col space-y-4 sm:space-y-8 top-4"}>
                <BookingLoginWidget className={"!mb-0"} />

                <BookingVoucher isFilter={true} />

                {hall && hall.hall_code !== HallCode.ArboPark && (
                    <>
                        <CardComponent>
                            <BookingDateFilter
                                disabled={false}
                                package={props.package}
                                register={form.register}
                                setValue={form.setValue}
                                showTitle={true}
                                onChange={onChangeDate}
                            />
                        </CardComponent>
                        <CardComponent>
                            <DriversCountFilter
                                isMinimalistic={false}
                                register={form.register}
                                selectedPackage={props.package}
                                setValue={form.setValue}
                                showTitle={true}
                                watch={form.watch}
                                onChange={onChangeDriversCount}
                            />
                        </CardComponent>
                    </>
                )}

                {ACTIVITIES_SELECTION_ALLOWED && activities.length > 1 && (
                    <CardComponent>
                        <ActivityFilter register={form.register} showTitle={true} watch={form.watch} onChange={onChangeActivity} />
                    </CardComponent>
                )}

                {GROUP_TYPES_ALLOWED && packages && packages.filter(Package => Package.extra.groupType !== null).length > 0 && (
                    <CardComponent>
                        <GroupTypeFilter register={form.register} showTitle={true} onChange={onChangeGroupType} />
                    </CardComponent>
                )}
            </div>
        );
    }
};

export default withRouter(BookingFilterMinimal);
